// HandTile.jsx
import React from 'react';
import './hand-tile.css';

// ── Color palette from Figma ──────────────────────────────────────────────────
const COLORS = {
  'raise-large': '#D44221',  // Large Raise  (medium red-orange)
  'raise-small': '#e5735a',  // Small Raise  (lighter red-orange)
  raise:         '#e5735a',  // Generic single-size raise (backward compat)
  allin:         '#8D2C16',  // Jam / All-In (dark red)
  call:          '#49ab78',  // Call / Check (green)
  fold:          '#d7d0c2',  // Fold / mixed remainder (warm gray)
  inRange:       '#b7b1a5',  // In original range but filtered out of current action (darker tan)
  oor:           '#d7d0c2',  // Out of range (never in the original range)
};

// Left-to-right display priority (highest priority = left side of gradient)
// 'inRange' (still in range, just a different action than the current filter)
// sits between real actions and true fold — it's neither an action being
// shown nor an out-of-range fold, just visually de-emphasized relative to both.
const LEFT_PRIORITY = ['allin', 'raise-large', 'raise-small', 'raise', 'call', 'inRange', 'fold'];
const priorityOf = (a) => { const i = LEFT_PRIORITY.indexOf(a); return i === -1 ? 99 : i; };

/**
 * Normalize a raw CSV action key to a COLORS key.
 *
 * raise_X keys are handled by getBackgroundStyle (needs global context).
 * This function handles everything else.
 *   limp / check → 'call'  (passive actions shown in green)
 *   raise-small / raise-large / allin / call / fold → pass through
 *   plain 'raise' → pass through (backward compat for non-blast ranges)
 */
const normalizeAction = (a) => {
  if (a === 'limp' || a === 'check') return 'call';
  return a;
};

/**
 * Derive a background style object from an actions map.
 *
 * globalRaiseSizes: sorted array of all raise_X keys seen across the full range
 * (e.g. ['raise_5', 'raise_8']). Used to determine whether a specific raise_X
 * is the "small" or "large" raise in context.
 *
 * Rules:
 *  – No entries                        → OOR cream
 *  – Actions sum to 100%               → solid color, or a hard split across
 *                                         each action's own color, left-to-right
 *                                         by priority (no fold segment needed)
 *  – Actions sum to < 100%             → same split, plus a trailing fold-gray
 *                                         segment for whatever's left over
 *
 * 'inRange' is not a real action — RangeMatrix attaches it to mean "still part
 * of the loaded range, just not the action this filter is showing". It's
 * treated as its own segment (darker tan) so it reads as different from true
 * fold (lighter tan), which is reserved for frequency that genuinely isn't in
 * the hand's actions at all.
 */
const getBackgroundStyle = (actions, globalRaiseSizes = []) => {
  // Merge/normalize all action keys into display keys.
  // raise_X keys are mapped to raise-small (smallest in range) or raise-large (others).
  const merged = {};
  Object.entries(actions || {}).forEach(([a, f]) => {
    let key;
    if (/^raise_/.test(a)) {
      // Determine small vs large based on position in the global raise size list
      if (globalRaiseSizes.length <= 1) {
        // Only one raise size in the entire range → use raise-small color
        key = 'raise-small';
      } else {
        // Multiple sizes: smallest gets raise-small, all others get raise-large
        key = (a === globalRaiseSizes[0]) ? 'raise-small' : 'raise-large';
      }
    } else {
      key = normalizeAction(a);
    }
    merged[key] = (merged[key] || 0) + (f || 0);
  });

  const entries = Object.entries(merged).filter(([, f]) => f > 0);

  if (entries.length === 0) {
    return { backgroundColor: COLORS.oor };
  }

  entries.sort(([a1], [a2]) => priorityOf(a1) - priorityOf(a2));

  const total = entries.reduce((s, [, f]) => s + f, 0);

  // Nothing missing and only one segment — plain solid color, no gradient needed.
  if (entries.length === 1 && total >= 100) {
    return { backgroundColor: COLORS[entries[0][0]] ?? COLORS.fold };
  }

  // Whatever isn't accounted for by a real action or an 'inRange' remainder is
  // true fold (out of range) — always the last/rightmost segment.
  const foldRemainder = Math.max(0, 100 - total);
  const stops = foldRemainder > 0 ? [...entries, ['fold', foldRemainder]] : entries;

  let acc = 0;
  const segments = stops.map(([action, freq]) => {
    const color = COLORS[action] ?? COLORS.fold;
    const start = acc;
    acc = Math.min(100, Math.round(acc + freq));
    return `${color} ${start}%, ${color} ${acc}%`;
  });

  return { background: `linear-gradient(to right, ${segments.join(', ')})` };
};

// ── Component ─────────────────────────────────────────────────────────────────

const HandTile = ({ text, type, isActive, actions = {}, globalRaiseSizes = [], onClick }) => {
  const getDisplayText = () => {
    if (type === 'pair' || (text.length === 2 && text[0] === text[1])) return text;
    const [first, second] = text.split('');
    if (type === 'suited')  return `${first}${second}s`;
    return `${first}${second}o`;
  };

  let backgroundStyle;
  if (isActive) {
    backgroundStyle = getBackgroundStyle(actions, globalRaiseSizes);
  } else {
    backgroundStyle = { backgroundColor: COLORS.oor };
  }

  const textColor  = isActive ? '#ffffff' : 'rgba(2,1,34,0.15)';
  const fontWeight = isActive ? 600 : 400;

  return (
    <div
      className="hand-tile"
      style={{ ...backgroundStyle, borderRadius: '24px' }}
      onClick={onClick}
    >
      <div className="hand-tile-text" style={{ color: textColor, fontWeight }}>
        {getDisplayText()}
      </div>
    </div>
  );
};

export default HandTile;
